import { Injectable } from '@angular/core';
import { PermissionType, ProfileLevel, ResourceNames } from '@enums/acl/resources.enum';
import { CognitoUserSessionDataI } from '@interfaces/cognito-user-data.interface';
import { UserLoginDataI } from '@interfaces/user-login-data.interface';
import { UserAttributesI, UserSocketLoginI } from '@interfaces/user.interface';
import { TeamsService } from '@modules/teams/teams.service';
import { AclAbilityService } from '@services/acl/acl.ability';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import * as cookieConst from '@constants/cookies-auth.const';

@Injectable({
  providedIn: 'any',
})
export class UserHelperService {
  private userData$ = new BehaviorSubject<UserAttributesI | undefined>(undefined);
  private static userChangedSubject = new BehaviorSubject<UserAttributesI | undefined>(undefined);
  private static userChanged$ = UserHelperService.userChangedSubject.asObservable();
  public static data: any = {};
  private static logoutSubject = new BehaviorSubject<boolean>(false);
  public static logout$ = UserHelperService.logoutSubject.asObservable();
  constructor(
    private teamsService: TeamsService,
    private aclService: AclAbilityService,
    private cookieService: CookieService
  ) {}

  public static notifyLogout() {
    UserHelperService.logoutSubject.next(true);
  }
  public getUserAttributes(): UserAttributesI | void {
    try {
      const userAttributes = localStorage.getItem('userAttributes');
      if (userAttributes) {
        const userAttr: UserAttributesI = JSON.parse(userAttributes);
        if (!Array.isArray(userAttr['custom:team'])) {
          userAttr['custom:team'] = userAttr['custom:team']?.split(',');
        }
        this.userData$.next(userAttr);
        return userAttr;
      }
    } catch (error) {
      throw new Error('UserHelperService getUserAttributes error: ' + error);
    }
  }
  public subscribeUserChange(value: Function): Subscription {
    return UserHelperService.userChanged$.subscribe((newUser) => value(newUser));
  }

  public isUsingOtherUser(): boolean {
    const atts = this.getUserAttributes();
    return !!atts?.admin_user;
  }

  public isUsingOtherUserAndCannotUpdate() {
    return this.isUsingOtherUser() && !this.isAllowedToUpdateEverything();
  }

  private isAllowedToUpdateEverything() {
    return this.aclService.can(PermissionType.UPDATE, ResourceNames.ADMINISTRATION__USERS);
  }

  public isNotAllowedToVisualizeContactData() {
    if (!this.isUsingOtherUser()) {
      return false;
    }

    return !(
      this.isUsingOtherUser() && this.aclService.can(PermissionType.UPDATE, ResourceNames.SUPERVISION__CONTACTS)
    );
  }

  public currentLevel(): number {
    const user: any = this.getUserAttributes() ?? {};
    return user.level ?? 0;
  }

  public isCurrentUserMidLevel(): boolean {
    const atts = this.getUserAttributes() ?? {};
    return ((<UserAttributesI>atts).level ?? 0) > ProfileLevel.LOW;
  }

  public isCurrentUserLowLevel(): boolean {
    const atts = this.getUserAttributes() ?? {};
    return ((<UserAttributesI>atts).level ?? 0) === ProfileLevel.LOW;
  }

  public isAdminOrHigher(): boolean {
    const atts = this.getUserAttributes() ?? {};
    return ((<UserAttributesI>atts).level ?? 0) >= ProfileLevel.HIGH;
  }

  public isSuperAdmin(): boolean {
    const atts = this.getUserAttributes() ?? {};
    return ((<UserAttributesI>atts).level ?? 0) > ProfileLevel.HIGH;
  }

  /** Super admin acting through "log in as customer": the attributes in use are the customer user's. */
  public isSuperAdminBehindLoginAs(): boolean {
    const admin = (this.getUserAttributes() as UserAttributesI | void)?.admin_user?.attributes;
    return (admin?.level ?? 0) > ProfileLevel.HIGH;
  }

  public hasOtherCustomerData() {
    const userData: any = UserHelperService.data || {};
    const user = this.getUserAttributes()!;
    if (userData.customer) {
      return user['custom:customer'] !== userData.customer;
    }
    return false;
  }

  public async setUserAttributes(userAttributes: UserAttributesI, forceDefaultTeamLoad?: boolean) {
    try {
      const needsDefaultTeam = !Array.isArray(userAttributes['custom:team']) || forceDefaultTeamLoad;
      this.persistUserAttributes(userAttributes);
      if (needsDefaultTeam) {
        await this.enrichDefaultTeam(userAttributes);
      }
    } catch (error) {
      throw new Error('UserHelperService setUserAttributes error: ' + error);
    }
  }

  // Writes the identity to localStorage synchronously — no network in the middle — so it can never
  // leave a window where the auth cookies already hold the new identity but localStorage still
  // holds the old one. defaultTeam is intentionally not required here (see enrichDefaultTeam).
  private persistUserAttributes(userAttributes: UserAttributesI): void {
    if (!Array.isArray(userAttributes['custom:team'])) {
      userAttributes['custom:team'] = String(userAttributes['custom:team'])
        .split(',')
        .filter((att) => att !== 'undefined');
    }
    this.tryNotifyChanged(userAttributes);
    localStorage.setItem('userAttributes', JSON.stringify(userAttributes));
  }

  // defaultTeam is a filter convenience, not identity, so its lookup runs OFF the critical persist
  // path and is merged in once it resolves. A failed/slow response never blocks or breaks the
  // session — the stored identity is already consistent. Bails if the session changed meanwhile.
  private async enrichDefaultTeam(userAttributes: UserAttributesI): Promise<void> {
    try {
      const defaultTeam = await firstValueFrom(
        this.teamsService.getTeamDefaultByCustomer(userAttributes['custom:customer'])
      );
      const storedRaw = localStorage.getItem('userAttributes');
      if (!storedRaw || JSON.parse(storedRaw)['custom:userId'] !== userAttributes['custom:userId']) {
        return;
      }
      userAttributes.defaultTeam = defaultTeam._id;
      if (Array.isArray(userAttributes['custom:team'])) {
        if (!userAttributes['custom:team'].includes(defaultTeam._id)) {
          userAttributes['custom:team'].push(defaultTeam._id);
        }
      } else {
        userAttributes['custom:team'] = [defaultTeam._id];
      }
      localStorage.setItem('userAttributes', JSON.stringify(userAttributes));
    } catch {
      // non-fatal — defaultTeam stays unset until the next successful load
    }
  }

  private tryNotifyChanged(newUserAttributes: UserAttributesI) {
    const oldAttributes = this.getUserAttributes();
    if (oldAttributes) {
      const oldUserId = oldAttributes['custom:userId'];
      const newUserId = newUserAttributes['custom:userId'];
      if (oldUserId && newUserId && newUserId !== oldUserId) {
        UserHelperService.userChangedSubject.next(newUserAttributes);
      }
    }
  }

  public setTemplateFields({ name, device, category, message, header, footer, variables, file, header_type }: any) {
    try {
      localStorage.setItem(
        'template',
        JSON.stringify({ name, device, category, message, header, footer, variables, file, header_type })
      );
    } catch (error) {
      throw new Error('UserHelperService setTemplateFields error: ' + error);
    }
  }

  public getTemplateFields() {
    try {
      const template = localStorage.getItem('template');
      if (template) {
        return JSON.parse(template);
      }
    } catch (error) {
      throw new Error('UserHelperService getTemplateFields error: ' + error);
    }
    return;
  }

  public removeTemplateField() {
    try {
      localStorage.removeItem('template');
    } catch (error) {
      throw new Error('UserHelperService removeTemplateField error: ' + error);
    }
    return;
  }

  getUser() {
    return this.userData$.asObservable();
  }

  updateUser() {
    const user = this.getUserAttributes();
    if (user) {
      this.userData$.next(user);
    }
  }

  public getIdToken(): string | undefined {
    try {
      const token = this.cookieService.get('idToken') || this.oldTokenFromLocalStorage('idToken');
      if (token) {
        return token;
      }
    } catch (error) {
      throw new Error('UserHelperService getToken error: ' + error);
    }
    return;
  }

  public setIdToken(token: string) {
    try {
      this.cookieService.set('idToken', token, 7, cookieConst.path, cookieConst.domain, true, cookieConst.sameSite);
    } catch (error) {
      throw new Error('UserHelperService setToken error: ' + error);
    }
  }

  public getRefreshToken(): string | undefined {
    try {
      const token = this.cookieService.get('refreshToken') || this.oldTokenFromLocalStorage('refreshToken');
      if (token) {
        return token;
      }
    } catch (error) {
      throw new Error('UserHelperService getRefreshToken error: ' + error);
    }
    return;
  }

  public getDefaultTeamFilter(): string {
    if (this.isAdminOrHigher()) {
      return '';
    }

    const user = this.getUserAttributes() ?? {};
    return (((user as UserAttributesI)['custom:team'] as string[]) ?? []).join(',');
  }

  public clearAccessTokens() {
    this.cookieService.delete('idToken', cookieConst.path, cookieConst.domain, true, cookieConst.sameSite);
    this.cookieService.delete('refreshToken', cookieConst.path, cookieConst.domain, true, cookieConst.sameSite);
  }

  /**
   * Provisório até a maioria dos tokens "migrarem" para os cookies
   */
  private oldTokenFromLocalStorage(tokenType: 'refreshToken' | 'idToken') {
    const oldToken = localStorage.getItem(tokenType);
    if (oldToken) {
      localStorage.removeItem(tokenType);
      if (tokenType === 'refreshToken') {
        this.setRefreshToken(oldToken);
      } else {
        this.setIdToken(oldToken);
      }
    }
    return oldToken;
  }

  public setRefreshToken(token: string) {
    try {
      this.cookieService.set(
        'refreshToken',
        token,
        7,
        cookieConst.path,
        cookieConst.domain,
        true,
        cookieConst.sameSite
      );
    } catch (error) {
      throw new Error('UserHelperService setToken error: ' + error);
    }
  }

  public getSocketLoginData(): UserSocketLoginI | void {
    const user = this.getUserAttributes();
    if (user) {
      return this.attributesAsUserSocketLoginI(user);
    }
    return;
  }
  public attributesAsUserSocketLoginI(user: UserAttributesI): UserSocketLoginI {
    return {
      _id: user['custom:userId'],
      customerId: user['custom:customer'],
      teams: user['custom:team'],
      team: user['custom:team'],
      name: user.name,
      avatar: user['custom:photo'] ?? user.picture ?? '',
      isPhone: user['custom:isPhone'],
      ramal: user['custom:ramal'],
      ramalHost: user['custom:ramalHost'],
      ramalPassword: user['custom:ramalPassword'],
      ramalPort: user.ramalPort,
      ramalURI: user.ramalURI,
      level: user.level ?? 0,
    } as UserSocketLoginI;
  }

  currentProfile(profiles: Array<any>): any {
    return this.aclService.currentProfile(profiles);
  }

  public verifyIfUserIsCurrentUser(userId: string): boolean {
    const user = this.getUserAttributes();
    if (user) {
      return user['custom:userId'] === userId;
    }
    return false;
  }

  public verifyIfTeamIsCurrentTeam(teamId: string): boolean {
    const user = this.getUserAttributes()!;
    return user['custom:team'].includes(teamId);
  }

  public async saveDataToken(token: CognitoUserSessionDataI) {
    const payload = token.idToken.payload;
    // Atomic identity write: cookies + roles + attributes persist as one synchronous burst (no
    // network in between), so even an unawaited caller or a slow team-default lookup can never
    // observe a split-brain state (cookie = new identity, localStorage = old). defaultTeam
    // enriches afterwards, off the critical path.
    this.setIdToken(token.idToken.jwtToken);
    this.setRefreshToken(token.refreshToken.token);
    this.aclService.saveRoles(token.roles, payload['custom:userId']);
    this.persistUserAttributes(payload);
    void this.enrichDefaultTeam(payload);
  }

  public integrations$ = new BehaviorSubject<string[] | undefined>(this.getIntegrations());

  public saveIntegrations(integrations: string[] | undefined) {
    if (!integrations) {
      return;
    }
    localStorage.setItem('integrations', JSON.stringify(integrations));
    this.integrations$.next(integrations);
  }

  private getIntegrations(): string[] | undefined {
    const value = localStorage.getItem('integrations');
    if (value) {
      return JSON.parse(value);
    }
    return undefined;
  }

  // browserId must survive here: every refresh reuses this payload, and an auth
  // born from refresh without it is invisible to the same-browser check that
  // suppresses the session-conflict modal (OMNI-7716).
  public setUserLoginData(userLoginData: UserLoginDataI) {
    localStorage.setItem('userLoginData', JSON.stringify(userLoginData));
  }

  public getUserLoginData(): UserLoginDataI {
    const data = localStorage.getItem('userLoginData');
    const userLoginData: UserLoginDataI = data ? JSON.parse(data) : {};
    return userLoginData;
  }
}
